"use client";

import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "next/navigation";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { CapabilityTabItem } from "@/types";
import LogoSlider from "@/components/ui/LogoSlider";
import SectionHeader from "@/components/ui/SectionHeader";

interface CapabilitiesTabsProps {
  tabs: CapabilityTabItem[];
}

const TRANSITION_SLOW = {
  duration: 0.85,
  ease: [0.16, 1, 0.3, 1],
} as const;

export default function CapabilitiesTabs({ tabs }: CapabilitiesTabsProps) {
  const searchParams = useSearchParams();
  const mobileNavContainerRef = useRef<HTMLDivElement>(null);

  // Read initial tab from search param or default
  const tabParam = searchParams.get("tab");
  const initialMatch = tabs.find(
    (t) => t.id.toLowerCase() === tabParam?.toLowerCase()
  );

  const [activeTabId, setActiveTabId] = useState<string>(
    initialMatch?.id || tabs[0]?.id || "civilian-training"
  );

  // Sync tab when query parameter changes
  const [prevParam, setPrevParam] = useState<string | null>(tabParam);
  if (tabParam !== prevParam) {
    setPrevParam(tabParam);
    if (initialMatch && initialMatch.id !== activeTabId) {
      setActiveTabId(initialMatch.id);
    }
  }

  useEffect(() => {
    if (!tabs || tabs.length <= 1) return;

    const interval = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      // Only autoscroll on mobile screens (Tailwind sm breakpoint is 640px)
      if (typeof window !== "undefined" && window.innerWidth >= 640) return;

      setActiveTabId((currentId) => {
        const currentIndex = tabs.findIndex((t) => t.id === currentId);
        const nextIndex = (currentIndex + 1) % tabs.length;
        const nextTabId = tabs[nextIndex]?.id || tabs[0]?.id;
        return nextTabId;
      });
    }, 10000);

    return () => clearInterval(interval);
  }, [tabs, activeTabId]);

  // On mobile: smoothly reset scroll to 0 so the active tab at 1st place is fully visible
  useEffect(() => {
    if (mobileNavContainerRef.current) {
      mobileNavContainerRef.current.scrollTo({ left: 0, behavior: "smooth" });
    }
  }, [activeTabId]);

  const handleTabChange = (tabId: string) => {
    setPrevParam(tabId);
    setActiveTabId(tabId);
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set("tab", tabId);
      window.history.replaceState({}, "", url.toString());
    }
  };

  const activeTab = tabs.find((t) => t.id === activeTabId) || tabs[0];

  const activeIndex = tabs.findIndex((t) => t.id === activeTabId);
  const safeActiveIndex = activeIndex >= 0 ? activeIndex : 0;

  // On mobile: active tab is always placed 1st, followed by remaining tabs in sequence
  const mobileTabs = [
    ...tabs.slice(safeActiveIndex),
    ...tabs.slice(0, safeActiveIndex),
  ];

  if (!tabs || tabs.length === 0) return null;

  return (
    <div
      id="capabilities-tabs"
      className="w-full scroll-mt-24 sm:scroll-mt-28"
    >
      {/* Desktop Tab Navigation (Preserves natural order with sliding indicator) */}
      <div className="hidden sm:block border-b border-white/15 overflow-x-auto no-scrollbar mb-8 sm:mb-10">
        <nav
          className="relative flex items-center gap-6 sm:gap-10 min-w-max pb-[1px]"
          aria-label="Capabilities tabs desktop"
        >
          {tabs.map((tab) => {
            const isActive = tab.id === activeTabId;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabChange(tab.id)}
                className={`group relative font-poppins text-base sm:text-base md:text-lg lg:text-2xl transition-colors duration-500 pb-3 sm:pb-6 text-left outline-none border-none select-none whitespace-nowrap cursor-pointer ${
                  isActive
                    ? "font-semibold text-white"
                    : "font-normal text-white/60 hover:text-white"
                }`}
              >
                <span>{tab.tabLabel}</span>

                {/* Framer Motion Sliding Active Indicator */}
                {isActive && (
                  <motion.span
                    layoutId="capabilities-active-tab-indicator-desktop"
                    className="absolute bottom-0 left-0 right-0 h-[2.5px] bg-white rounded-full pointer-events-none"
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Mobile Tab Navigation (Active tab shows in 1st place always) */}
      <div
        ref={mobileNavContainerRef}
        className="block sm:hidden border-b border-white/15 overflow-x-auto no-scrollbar mb-8"
      >
        <nav
          className="relative flex items-center gap-6 min-w-max pb-[1px]"
          aria-label="Capabilities tabs mobile"
        >
          {mobileTabs.map((tab) => {
            const isActive = tab.id === activeTabId;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabChange(tab.id)}
                className={`group relative font-poppins text-base transition-colors duration-500 pb-3 text-left outline-none border-none select-none whitespace-nowrap cursor-pointer ${
                  isActive
                    ? "font-semibold text-white"
                    : "font-normal text-white/60 hover:text-white"
                }`}
              >
                <span>{tab.tabLabel}</span>

                {/* Framer Motion Sliding Active Indicator for Mobile */}
                {isActive && (
                  <motion.span
                    layoutId="capabilities-active-tab-indicator-mobile"
                    className="absolute bottom-0 left-0 right-0 h-[2.5px] bg-white rounded-full pointer-events-none"
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Main Tab Content Card */}
      <div className="bg-brand-card rounded-[10px] p-5 sm:p-8 md:p-8 lg:p-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-8 xl:gap-12 2xl:gap-[100px] items-center mb-8 sm:mb-10">
          {/* Left Column: Number + Content */}
          <div className="lg:col-span-7 min-w-0 flex flex-col sm:flex-row items-start gap-4 sm:gap-6 lg:gap-6 xl:gap-10 2xl:gap-[100px]">
            {/* Header row on mobile (Number + Title inline) */}
            <div className="flex sm:hidden items-center gap-4 w-full">
              {/* Step Counter (Mobile) */}
              <div>
                <span className="font-poppins text-base text-cream ">{activeTab.tabNumber}</span>
                <span className="font-poppins text-base text-cream ml-0.5">
                  /{activeTab.totalTabs}
                </span>
              </div>

              {/* Title (Mobile) */}
              <h3 className="font-poppins font-bold text-base text-center items-center text-cream">
                {activeTab.title}
              </h3>
            </div>

            {/* Step Counter (Tablet / Desktop - Clock odometer roll) */}
            <div className="hidden sm:flex shrink-0 items-baseline select-none">
              <div
                style={{ overflowY: "clip", overflowX: "visible" }}
                className="relative font-oswald text-5xl sm:text-6xl md:text-7xl lg:text-7xl xl:text-8xl font-normal leading-none text-white h-[1.15em] inline-flex items-center justify-center transition-[width] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
              >
                {/* Sizing anchor: gives exact natural width of current digit so /4 is snug */}
                <span
                  className="opacity-0 pointer-events-none select-none invisible"
                  aria-hidden="true"
                >
                  {activeTab.tabNumber}
                </span>

                <AnimatePresence initial={false}>
                  <motion.span
                    key={activeTab.id}
                    initial={{ y: "115%" }}
                    animate={{ y: "0%" }}
                    exit={{ y: "-115%" }}
                    transition={TRANSITION_SLOW}
                    className="absolute inset-0 flex items-center justify-center text-white will-change-transform"
                  >
                    {activeTab.tabNumber}
                  </motion.span>
                </AnimatePresence>
              </div>

              <span className="font-poppins text-base sm:text-lg md:text-xl text-white/50 ml-0.5 sm:ml-1">
                /{activeTab.totalTabs}
              </span>
            </div>

            {/* Content Text (Smooth sliding text) */}
            <div className="relative w-full min-w-0 pt-0 sm:pt-2">
              <AnimatePresence mode="popLayout" initial={false}>
                <motion.div
                  key={activeTab.id}
                  initial={{ y: 32, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -32, opacity: 0 }}
                  transition={TRANSITION_SLOW}
                  className="w-full will-change-transform"
                >
                  {/* Title (Hidden on mobile because it's rendered inline with counter above) */}
                  <h3 className="hidden sm:block font-poppins font-semibold text-lg sm:text-xl md:text-2xl text-white mb-2 sm:mb-3">
                    {activeTab.title}
                  </h3>
                  <p className="font-poppins font-normal text-base sm:text-base text-cream leading-relaxed">
                    {activeTab.description}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Right Column: Image Reel (Seamless vertical transition, no black gap) */}
          <div className="lg:col-span-5 min-w-0 w-full">
            <div className="relative w-full aspect-[16/10] sm:aspect-[16/9] lg:aspect-[16/10] rounded-[10px] overflow-hidden bg-neutral-900 border border-white/10 group">
              <AnimatePresence initial={false}>
                <motion.div
                  key={activeTab.id}
                  initial={{ y: "100%" }}
                  animate={{ y: "0%" }}
                  exit={{ y: "-100%" }}
                  transition={TRANSITION_SLOW}
                  className="absolute inset-0 w-full h-full will-change-transform"
                >
                  <Image
                    src={activeTab.image.src}
                    alt={activeTab.image.alt}
                    fill
                    sizes="(max-width: 768px) 100vw, 50vw"
                    priority
                    className="object-cover object-center transform transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </motion.div>
              </AnimatePresence>

              {/* Seamless Vignette Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Esteemed Partners Section */}
        {activeTab.logos && activeTab.logos.length > 0 && (
          <div className="overflow-hidden pt-0 sm:pt-10">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={activeTab.id}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -18 }}
                transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
              >
                {/* Label with Green Square Indicator */}
                <SectionHeader title={activeTab.clientsServedLabel || ""} />

                {/* LogoSlider component */}
                <div className="w-full overflow-hidden">
                  <LogoSlider
                    logos={activeTab.logos}
                    showNames={true}
                    cardClassName="p-4 sm:p-5 h-32 sm:h-36 w-[220px] sm:w-[260px]"
                    itemClassName="w-[220px] sm:w-[260px]"
                    imageClassName="h-28 sm:h-32 w-auto max-w-[90%] object-contain opacity-90 hover:opacity-100 transition-all duration-300 bg-transparent p-0 scale-115 hover:scale-120"
                    imageWidth={380}
                    imageHeight={200}
                    mobileCardClassName="py-3 px-2 h-32"
                    mobileImageClassName="max-h-[52px] w-auto max-w-[85%] object-contain opacity-90 bg-transparent p-1 rounded"
                    nameClassName="text-base font-semibold text-brand-cream/80 text-center leading-snug line-clamp-2"
                    mobileNameClassName="text-[11px] font-medium text-brand-cream/90 text-center leading-tight line-clamp-2 px-0.5"
                  />
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}
